import React, { PropsWithChildren, useEffect } from "react";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import Image, { ImageProps } from "next/image";
import { Button as _Button } from "@/v2-components/ui/button";
import { useRouter } from "next/router";
import { GameResell } from "@/models/GameResell";
import { MetaQuestLogo } from "@/v2-components/core/icon/meta-quest-logo";
import { useCurrency } from "@/hooks/useCurrency";
import { formatMoney } from "@/utils/CommonUtils";
import { GamesButtonWebView } from "@/components/webview/games/shared/GamesButtonWebView";
import {
  Body4,
  Button2,
  Subtitle2,
} from "@/components/webview/games/typograpy/typograpy-webview";
import AutoplayCarouselWebView from "@/components/webview/shared/AutoplayCarouselWebView";
import { useBannersWebView } from "@/components/webview/games/hooks/useBanners";
import { GamePageImageBannerTemplateWebview } from "@/components/webview/games/GamePageImageBannerTemplateWebview";
import { useLocale } from "@/store/locale-store";
import { useSellableGames } from "@/components/webview/games/hooks/useBhapticsContentsWebView";
import { GameUtil } from "@/utils/GameUtil";

const SQUARE_IMAGES = [
  {
    name: "Into the Radius",
    src: `/images/compatibility/vr/Into the Radius.png`,
  },
  { name: "Hitstream", src: `/images/compatibility/vr/Hitstream.png` },
  {
    name: "Medieval Dynasty New Settlement",
    src: `/images/compatibility/vr/Medieval Dynasty New Settlement.png`,
  },
];

const useQuestGameResells = () => {
  const { games, isLoading } = useSellableGames("quest");

  return {
    isLoading,
    gameResells: games
      .map((g) => g.gameResell)
      .filter((g) => !!g)
      .sort((a, b) => {
        const indexA = SQUARE_IMAGES.findIndex(({ name }) => a.name === name);
        const indexB = SQUARE_IMAGES.findIndex(({ name }) => b.name === name);

        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1 && indexB === -1) return -1;
        if (indexA === -1 && indexB !== -1) return 1;

        return 0;
      })
      .map((g) => {
        const find = SQUARE_IMAGES.find(({ name }) => g.name === name);
        const thumbnailImageLink = find?.src ?? g.thumbnailImageLink;
        return {
          ...g,
          thumbnailImageLink,
        };
      })
      .filter((g, i) => i < 3),
  };
};

export const GameIntroWebView = ({ className }: { className?: string }) => {
  const { gameResells, isLoading } = useQuestGameResells();
  const { t } = useTrans(["games"]);
  const [_, setInit] = React.useState(false); // 강제렌더링
  const router = useRouter();
  const { data } = useBannersWebView("Game", "MOP");
  const { languageCode } = useLocale();

  useEffect(() => {
    setInit(true);
  }, []);

  const filteredData = data?.filter((item) => {
    return !!item.webMobileImageUrl;
  });

  // 「게임 키 판매 중」 슬라이드는 **살 수 있는 키가 있을 때만**(#6054 R1). 목록(`useSellableGames`)이 판매 제외 국가 ·
  // 이 통화 가격 없는 키를 빼므로, 한국에선 비어 문구와 「Learn More」(→ 빈 목록)만 남았다 — prod 판매 게임 키 6개가 전부
  // KR 판매 제외(`disableCountryList`)다(그중 Hitstream · Medieval Dynasty 는 원화 가격도 있지만 판매 제외가 먼저 막는다,
  // prod sdk 실측 2026-10-21).
  const hasGameKeys = gameResells.length > 0;
  const totalItems = (filteredData?.length ?? 0) + (hasGameKeys ? 1 : 0);

  return (
    <div className={cn("select-none", className)}>
      <AutoplayCarouselWebView isLoading={isLoading} totalItems={totalItems}>
        <div className="flex max-h-[600px]">
          {filteredData?.map((banner, index) => (
            <div key={index} className="relative w-full shrink-0 grow-0">
              <GamePageImageBannerTemplateWebview
                {...banner}
                languageCode={languageCode}
              />
            </div>
          ))}

          {hasGameKeys && (
            <Banner
              onClickButton={() => router.push("search/?only-sellable=true")}
              className="text-coolgray-90"
              buttonText={t("Learn More")}
              body={t("Grab your favorite games and feel the immersion!")}
              title={t("Game Keys Now Available at Bhaptics!")}
              mobileImageSrc="/images/games/game_intro_1-mobile@3x.jpg"
              imageSrc={"/images/games/game_intro_1@2x.png"}
            >
              <div className="hidden h-full grow items-center justify-end gap-x-7.5 pr-5 laptop:flex desktop:pr-10">
                <MetaQuestLogo className="w-[213px]" />
                <GameCards gameResells={gameResells} />
              </div>
              <div className="flex h-full grow flex-col items-center px-2.5 py-3.5 laptop:hidden">
                <Image
                  src={"/svgs/platform-quest2.svg"}
                  width={117}
                  height={42}
                  alt="meta quest logo"
                />
                <GameCards gameResells={gameResells} />
              </div>
            </Banner>
          )}
        </div>
      </AutoplayCarouselWebView>
    </div>
  );
};

function Card({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={cn(className)}>{children}</div>;
}

Card.BG = function ImageCard({
  className = "",
  alt = "",
  ...props
}: ImageProps) {
  return (
    <div
      className={cn("absolute inset-0 aspect-[1440/320] size-full", className)}
    >
      <Image
        className={cn("size-full rounded-2xl object-cover")}
        fill
        alt={alt}
        {...props}
      />
    </div>
  );
};

Card.MobileBG = function MobileImageCard({
  className = "",
  alt = "",
  ...props
}: ImageProps) {
  return (
    <div
      className={cn("absolute inset-0 aspect-[360/400] size-full", className)}
    >
      <Image
        className={cn("size-full object-cover object-top", className)}
        fill
        sizes={"100vw"}
        alt={alt}
        {...props}
      />
    </div>
  );
};

Card.Header = function Header({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) {
  return <h2 className={cn("text-4xl text-white", className)}>{children}</h2>;
};

Card.Body = function Body({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <p className={cn("whitespace-pre-wrap text-sm laptop:text-lg", className)}>
      {children}
    </p>
  );
};

Card.Title = function Title({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <h2
      className={cn("whitespace-pre-wrap text-lg laptop:text-4xl", className)}
    >
      {children}
    </h2>
  );
};

Card.Content = function Content({
  className,
  children,
  title,
  body,
}: PropsWithChildren<{
  className?: string;
  title?: React.ReactNode;
  body?: React.ReactNode;
}>) {
  return (
    <div className={cn("space-y-2 laptop:space-y-3", className)}>
      {title && <Subtitle2>{title}</Subtitle2>}
      {children}
      {body && <Body4>{body}</Body4>}
    </div>
  );
};

Card.Button = function Button({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"button">) {
  return (
    <_Button
      variant={"blue-fill"}
      shape={"pill"}
      size={"sm"}
      className={cn("px-6", className)}
      {...props}
    />
  );
};

const GameCards = ({ gameResells }: { gameResells: GameResell[] }) => {
  const currency = useCurrency();

  return (
    <div
      className={cn(
        "flex h-full w-full justify-center gap-x-2 laptop:w-auto laptop:gap-x-3 laptop:py-7.5",
      )}
    >
      {gameResells.map((game, idx) => {
        const { priceList } = game.platformList[0];
        const {
          price,
          discount,
          discountPercentile,
          currency: _currency,
        } = GameUtil.findPrice(priceList, currency) ?? priceList[0];
        return (
          <GameCard
            key={idx}
            imageSrc={game.thumbnailImageLink}
            name={game.name}
            price={price}
            discount={discount}
            discountPercentile={discountPercentile}
            currency={_currency}
          />
        );
      })}
    </div>
  );
};

type GameProps = {
  imageSrc: string;
  name: string;
  price: number;
  discount: number;
  discountPercentile: number;
  currency: string;
};

const GameCard = ({
  imageSrc,
  name,
  price,
  discount,
  discountPercentile,
  currency,
}: GameProps & { className?: string }) => {
  const { t } = useTrans(["gnb"]);
  const percentOff = t("{{percent}}% off", {
    percent: discountPercentile,
    defaultValue: `${discountPercentile}% off`,
  });
  return (
    <div className="relative flex max-w-[33vw] flex-1 flex-col overflow-hidden rounded-md laptop:max-w-[162px] desktop:aspect-[162/260]">
      <div className="relative aspect-square laptop:w-[162px]">
        <Image src={imageSrc} fill className="object-cover" alt={name} />
      </div>
      <div className="flex max-h-[100px] grow flex-col items-center justify-around rounded-b bg-white px-1 py-1.5 text-center laptop:gap-x-2 laptop:py-2">
        <h2 className="line-clamp-2 text-sm font-600">{name}</h2>
        <div>
          {!!discountPercentile && (
            <div className="flex items-center justify-center gap-1">
              <p className="text-sm font-600 text-coolgray-90">{percentOff}</p>
              <p className="text-sm text-coolgray-50 line-through">
                {formatMoney({
                  amount: price,
                  currency,
                  hideCurrency: true,
                })}
              </p>
            </div>
          )}
          <h4 className="text-lg font-400 text-red-60">
            {formatMoney({
              amount: price - discount,
              currency,
              hideCurrency: true,
            })}
          </h4>
        </div>
      </div>
    </div>
  );
};

const Banner = ({
  imageSrc,
  mobileImageSrc,
  title,
  body,
  buttonText,
  onClickButton,
  className,
  children,
}: React.PropsWithChildren<{
  imageSrc: string;
  mobileImageSrc: string;
  title: string;
  body?: string;
  buttonText?: string;
  onClickButton: (e: any) => void;
  className?: string;
}>) => {
  return (
    <Card className={cn("relative w-full shrink-0 grow-0", className)}>
      <Card.BG src={imageSrc} alt="" className="hidden laptop:block" />
      <Card.MobileBG
        src={mobileImageSrc}
        alt=""
        className="block rounded-lg laptop:hidden"
      />
      <div className="relative hidden size-full min-h-[320px] items-center gap-x-2 laptop:flex"></div>
      <div className="relative flex size-full min-h-[400px] flex-col justify-end px-1 pb-11 text-center laptop:hidden">
        {children}
        <div className="px-5">
          <Card.Content title={title} body={body} />
          <GamesButtonWebView onClick={onClickButton} className="mt-4">
            <Button2>{buttonText}</Button2>
          </GamesButtonWebView>
        </div>
      </div>
    </Card>
  );
};
